import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { useCart } from '../../context/CartContext';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { startOnlinePayment, waitForPayment, cancelPayment } from '../../services/payment';
import PageHeader from '../../components/PageHeader';
import {
  Button, Input, Row, Empty,
} from '../../components/Ui';
import ChargeRows from '../../components/ChargeRows';
import { Reveal, Pop, Pulse } from '../../components/Motion';
import AddressPicker from './AddressPicker';
import { colors } from '../../theme';
import { currency } from '../../utils/format';

const CheckoutPage = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { carts, refresh } = useCart();
  const { config } = useApp();
  const toast = useToast();

  // Reloading the checkout URL directly has no navigation state, so fall back
  // to the cart this context already holds for that shop.
  const cart = (state && state.cart)
    || carts.find((c) => String(c.partner && c.partner._id) === String(partnerId));

  // Online payment only shows up once the client has added Razorpay keys in
  // Admin -> Settings (surfaced through /config).
  /**
   * Online payment appears only once Razorpay keys exist in Admin -> Settings.
   * Default to OFF: the previous check treated a missing config as "enabled",
   * so the option showed before any keys were saved and led to a dead checkout.
   */
  const onlineEnabled = Boolean(config && config.features && config.features.onlinePayment);

  const [addressId, setAddressId] = useState(null);
  const [paymentMode, setPaymentMode] = useState('cod');
  const [note, setNote] = useState('');
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [paymentId, setPaymentId] = useState('');
  const [payError, setPayError] = useState('');
  /**
   * form | paying | failed | done.
   *
   * The order row has to exist before Razorpay can charge against it, but an
   * unpaid order is not a placed order as far as the customer is concerned -
   * showing "Order placed!" and then asking for money read as a bug.
   */
  const [phase, setPhase] = useState('form');
  const pollRef = useRef(null);

  // Stop polling if the page goes away mid-payment.
  useEffect(() => () => { if (pollRef.current && pollRef.current.cancel) pollRef.current.cancel(); }, []);

  const onAddresses = useCallback((list) => {
    const preferred = list.find((a) => a.isDefault) || list[0];
    if (preferred) setAddressId((cur) => cur || preferred._id);
  }, []);

  const payForOrder = async (order) => {
    setPayError('');
    setPhase('paying');
    window.scrollTo({ top: 0 });
    try {
      // The checkout sheet verifies server-side before it resolves, so a
      // 'paid' result here is already final; polling only covers the fallback.
      const sheet = await startOnlinePayment({ refType: 'order', refId: order._id });
      const pid = sheet.paymentId;

      let result = sheet;
      if (sheet.status !== 'paid') {
        pollRef.current = waitForPayment(pid);
        result = await pollRef.current;
      }

      if (result.status === 'paid') {
        setPaymentId((result.payment && result.payment.providerPaymentId)
          || (sheet.payment && sheet.payment.providerPaymentId) || '');
        setPlaced((o) => (o ? { ...o, paymentStatus: 'paid' } : o));
        await refresh();
        setPhase('done');
        return;
      }

      if (result.status === 'timeout') {
        setPayError('We have not received the payment yet. If money was deducted it will reflect shortly.');
      } else {
        setPayError('The payment did not go through. Nothing has been charged.');
        await cancelPayment(pid, 'Payment not completed');
      }
      setPhase('failed');
    } catch (err) {
      setPayError(err.cancelled
        ? 'You closed the payment before it finished.'
        : (err.message || 'The payment could not be started.'));
      setPhase('failed');
    }
  };

  /** Give up on paying online and let the rider collect cash instead. */
  const switchToCod = async () => {
    try {
      await api.orders.setPaymentMode(placed._id, { paymentMode: 'cod' });
      setPlaced((o) => ({ ...o, paymentMode: 'cod' }));
      await refresh();
      setPhase('done');
    } catch (err) {
      toast.error(err.message, 'Could not switch');
    }
  };

  if (!cart) {
    return (
      <Empty
        emoji="🛒"
        title="This cart is no longer open"
        text="It may already have been checked out. Open your cart to start again."
        actionLabel="Go to cart"
        onAction={() => navigate('/cart')}
      />
    );
  }

  const placeOrder = async () => {
    if (!addressId) {
      toast.error('Add a delivery address to continue.', 'Address needed');
      return;
    }
    setPlacing(true);
    try {
      const res = await api.orders.create({
        partnerId: cart.partner._id,
        addressId,
        paymentMode,
        note,
      });
      setPlaced(res.data);
      window.scrollTo({ top: 0 });
      if (paymentMode === 'online') {
        payForOrder(res.data);
      } else {
        await refresh();
        setPhase('done');
      }
    } catch (err) {
      toast.error(err.message, 'Could not place order');
    } finally {
      setPlacing(false);
    }
  };

  /* ----------------------- waiting for the money ----------------------- */
  if (phase === 'paying') {
    return (
      <div className="center-page">
        <div style={{ maxWidth: 440, width: '100%' }}>
          <Pulse>
            <div style={{
              width: 104, height: 104, borderRadius: 52, background: colors.primaryLight,
              display: 'grid', placeItems: 'center', fontSize: 44, margin: '0 auto',
            }}
            >
              💳
            </div>
          </Pulse>
          <h1 className="h1" style={{ marginTop: 20 }}>Waiting for payment</h1>
          <p className="t-body" style={{ color: colors.muted, marginTop: 8, lineHeight: 1.6 }}>
            Finish the payment in the Razorpay window. Your order is confirmed the moment it goes
            through - keep this page open.
          </p>
          <div className="card sh-sm" style={{ marginTop: 20, textAlign: 'left' }}>
            <Row label="Paying" value={currency(placed && placed.totalAmount)} valueStyle={{ fontWeight: 800 }} />
            <Row label="To" value={cart.partner && cart.partner.businessName} />
          </div>
        </div>
      </div>
    );
  }

  /* --------------------------- payment failed --------------------------- */
  if (phase === 'failed') {
    return (
      <div className="center-page">
        <div style={{ maxWidth: 440, width: '100%' }}>
          <Pop>
            <div style={{
              width: 104, height: 104, borderRadius: 52, background: colors.dangerBg,
              display: 'grid', placeItems: 'center', fontSize: 44, margin: '0 auto',
            }}
            >
              ❌
            </div>
          </Pop>
          <h1 className="h1" style={{ marginTop: 20, color: colors.danger }}>Payment failed</h1>
          <p className="t-body" style={{ color: colors.muted, marginTop: 8, lineHeight: 1.6 }}>
            {payError} Your order is held for you - pay again or switch to cash on delivery and it
            goes through straight away.
          </p>
          <div className="col" style={{ gap: 12, marginTop: 24 }}>
            <Button title="Retry payment" icon="💳" size="lg" full onClick={() => payForOrder(placed)} />
            <Button title="Pay cash on delivery instead" variant="light" full onClick={switchToCod} />
            <button type="button" className="btn ghost" onClick={() => navigate('/orders')}>
              I will do this later - it is in My Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------ confirmed ----------------------------- */
  if (phase === 'done') {
    const paidOnline = placed.paymentStatus === 'paid';
    return (
      <div className="center-page">
        <div style={{ maxWidth: 440, width: '100%' }}>
          <Pop>
            <div
              style={{
                width: 104, height: 104, borderRadius: 52, background: colors.successBg,
                display: 'grid', placeItems: 'center', fontSize: 46, margin: '0 auto',
              }}
            >
              🎉
            </div>
          </Pop>
          <h1 className="h1" style={{ marginTop: 20 }}>Order placed!</h1>
          <p className="t-body" style={{ color: colors.muted, marginTop: 8, lineHeight: 1.6 }}>
            {paidOnline
              ? `Payment received. ${(cart.partner && cart.partner.businessName) || 'The shop'} has your order and will confirm it shortly.`
              : `${(cart.partner && cart.partner.businessName) || 'The shop'} has received your order and will confirm it shortly.`}
          </p>

          {paidOnline ? (
            <div className="card" style={{ marginTop: 16, background: colors.successBg, border: 0 }}>
              <span style={{ fontSize: 26 }}>✅</span>
              <div className="t-title" style={{ marginTop: 6, color: colors.success }}>Payment received</div>
              {paymentId ? (
                <div className="t-tiny" style={{ marginTop: 2 }}>Payment ID · {paymentId}</div>
              ) : null}
            </div>
          ) : null}

          <div className="card sh-sm" style={{ marginTop: 16, textAlign: 'left' }}>
            <Row label="Order number" value={placed.orderNo} />
            <Row label="Items" value={String(placed.items && placed.items.length)} />
            <Row label="Payment" value={placed.paymentMode === 'cod' ? 'Cash on delivery' : 'Paid online'} />
            {paidOnline && paymentId ? <Row label="Payment ID" value={paymentId} /> : null}
            <Row label="Total" value={currency(placed.totalAmount)} valueStyle={{ fontWeight: 800 }} />
          </div>

          <div className="col" style={{ gap: 12, marginTop: 24 }}>
            <Button
              title="Track my order"
              size="lg"
              full
              onClick={() => navigate(`/orders/${placed._id}`, { replace: true })}
            />
            <Button title="Continue shopping" variant="light" full onClick={() => navigate('/home')} />
          </div>
        </div>
      </div>
    );
  }

  const PAYMENT_OPTIONS = [
    { key: 'cod', icon: '💵', title: 'Cash on delivery', text: 'Pay the delivery person directly' },
    ...(onlineEnabled
      ? [{ key: 'online', icon: '💳', title: 'Pay online', text: 'UPI, card or netbanking via Razorpay' }]
      : []),
  ];

  return (
    <div className="has-action-bar">
      <PageHeader title="Checkout" subtitle={cart.partner && cart.partner.businessName} />

      <div className="split">
        <div>
          <Reveal>
            <AddressPicker value={addressId} onChange={setAddressId} onLoaded={onAddresses} />
          </Reveal>

          <Reveal delay={80} className="card sh-sm" style={{ marginTop: 16 }}>
            <h3 className="t-title" style={{ marginBottom: 12 }}>Payment method</h3>
            {PAYMENT_OPTIONS.map((p) => {
              const active = paymentMode === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPaymentMode(p.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                    background: active ? colors.primaryLight : '#fff',
                    border: `1.4px solid ${active ? colors.primary : colors.line}`,
                    borderRadius: 16, padding: 14, marginBottom: 8,
                    transition: 'border-color .16s ease, background .16s ease',
                  }}
                >
                  <span style={{ fontSize: 22 }}>{p.icon}</span>
                  <span className="grow">
                    <span className="t-title" style={{ display: 'block' }}>{p.title}</span>
                    <span className="t-small" style={{ display: 'block', marginTop: 2 }}>{p.text}</span>
                  </span>
                  <span
                    style={{
                      width: 20, height: 20, borderRadius: 10, flex: '0 0 auto',
                      border: `${active ? 6 : 2}px solid ${active ? colors.primary : colors.line}`,
                    }}
                  />
                </button>
              );
            })}
          </Reveal>

          <Reveal delay={140} className="card sh-sm" style={{ marginTop: 16 }}>
            <Input
              label="Note for the shop (optional)"
              textarea
              rows={2}
              placeholder="Ring the bell twice, leave at the gate…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{ marginBottom: 0 }}
            />
          </Reveal>
        </div>

        <div className="split-side">
          <div className="action-bar">
            <div className="action-extra">
              <h3 className="t-title" style={{ marginBottom: 10 }}>Order summary</h3>
              {cart.items.map((line) => (
                <Row
                  key={line._id || (line.product && line.product._id)}
                  label={`${line.product && line.product.title} × ${line.quantity}`}
                  value={currency(line.price * line.quantity)}
                />
              ))}
              <div className="divider" />
              <Row label="Item total" value={currency(cart.subtotal)} />
              <Row label="Delivery" value={cart.deliveryCharge ? currency(cart.deliveryCharge) : 'Free'} />
              <ChargeRows doc={cart} />
              <div className="divider" />
            </div>

            <div className="action-main">
              <div className="t-tiny">TO PAY</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{currency(cart.totalAmount)}</div>
            </div>

            <div className="action-cta">
              <Button title="Place order" onClick={placeOrder} loading={placing} full />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
